import React from 'react';
import { Link } from 'react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  InputAdornment,
  LinearProgress,
  styled as MuiStyled,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  debounce,
  tableCellClasses,
} from '@mui/material';

import styled from '@emotion/styled';
import dayjs from 'dayjs';

import { Invite, VenueListInvitesFilter } from '../../../graphql/API';
import { useListInvitesV2Query } from '../../../hooks/reactQuery/useListInvitesV2Query';
import { useVenueCancelInviteMutation } from '../../../hooks/reactQuery/useVenueCancelInviteMutation';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import Icons from '../../../themes/icons';
import { formatNumberWithCommas } from '../../../utils/formatter';
import SvgIcon, { IconName } from '../../Icons';
import { InviteStatus } from './InviteStatus';
import { InviteType } from '../../../API';

const StyledTableCell = MuiStyled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.white,
    color: '#848484',
    borderBottom: '1px solid #E0E0E0',
    fontSize: '15px',
    fontWeight: 400,
    padding: '0 0 4px 0',
    margin: 0,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '15px',
    padding: '16px 0',
  },
}));

const StyledTableRow = MuiStyled(TableRow)(() => ({
  '&': {
    backgroundColor: 'transparent',
  },
  '& td, & th': {
    borderBottom: '1px solid #E0E0E0',
  },
}));

const REQUIREMENT_ICON_MAP: Record<string, string> = {
  postOnInstagramStories: 'Story',
  postOnInstagramStories2x: 'Story',
  postOnInstagramStories3x: 'Story',
  postOnInstagramFeed: 'Feed',
  postOnInstagramReels: 'Reel',
  postOnTikTokVideo: 'TikTok',
};

const REQUIREMENT_MAP: Record<string, string> = {
  postOnInstagramStories: 'Story',
  postOnInstagramStories2x: '2 Stories',
  postOnInstagramStories3x: '3 Stories',
  postOnInstagramFeed: 'Feed',
  postOnInstagramReels: 'Reel',
  postOnTikTokVideo: 'TikTok',
};

type Props = {
  venueID: string;
  handleUpdateLastInvite?: (val: string) => void;
};

function hoursRemaining(expiry: string | null | undefined): number {
  if (!expiry) return 0;
  const diff = dayjs(expiry).diff(dayjs(), 'hour', true);
  return Math.max(0, Math.floor(diff));
}

export const CollabInvitesTable: React.FC<Props> = ({ venueID, handleUpdateLastInvite }) => {
  const [searchText, setSearchText] = React.useState('');
  const [cancelInvite, setCancelInvite] = React.useState<Invite | null>(null);

  const [filter, setFilter] = React.useState<Partial<VenueListInvitesFilter>>({
    sortBy: 'invites.updatedAt',
    sortDirection: 'desc',
  });

  const {
    data: invites,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useListInvitesV2Query(venueID, { ...filter });

  const cancelInviteMutation = useVenueCancelInviteMutation({
    venueID,
    onSuccess: () => setCancelInvite(null),
  });

  const { lastElementRef } = useInfiniteScroll({
    hasMore: hasNextPage,
    isFetching: isFetching || isLoading,
    cb: fetchNextPage,
  });

  React.useEffect(() => {
    if (invites.length > 0 && handleUpdateLastInvite) {
      handleUpdateLastInvite(invites[0].updatedAt || '');
    }
  }, [invites]);

  const searchDebounce = React.useMemo(
    () => debounce((searchText: string) => setFilter((prev) => ({ ...prev, searchText })), 500),
    [],
  );

  React.useEffect(() => {
    searchDebounce(searchText);
  }, [searchText, searchDebounce]);

  const handleTextOnChange = React.useCallback<React.ChangeEventHandler<HTMLTextAreaElement>>(
    (e) => setSearchText(e.target.value),
    [setSearchText],
  );

  const headerCells: Array<Record<string, string | boolean | number>> = React.useMemo(
    () => [
      { id: 'picture', label: '', sortable: false, align: 'left' },
      { id: 'users.firstname', label: 'User', sortable: true, align: 'left' },
      { id: 'offers.title', label: 'Offer', sortable: true, align: 'left' },
      { id: 'invites.updatedAt', label: 'Invite date', sortable: true, align: 'left' },
      { id: 'invites.requirements', label: 'Requirement', sortable: true, align: 'left' },
      { id: 'invites.offerPrice', label: 'Cost', sortable: true, align: 'right' },
      { id: 'invites.status', label: 'Status', sortable: true, align: 'center' },
      { id: 'action', label: 'Action', sortable: false, align: 'center' },
    ],
    [],
  );

  return (
    <>
      <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} mb={3}>
        <OutlinedInput
          id='search-offers'
          placeholder='Search a member...'
          startAdornment={
            <InputAdornment position='start'>
              <SearchIcon />
            </InputAdornment>
          }
          value={searchText}
          onChange={handleTextOnChange}
          size='small'
          sx={{ border: '1px solid #EFEEEE', minWidth: '380px' }}
        />
      </Box>
      {(isLoading || isFetching) && <LinearProgress sx={{ mt: 2 }} />}
      <TableContainer component={'div'} sx={{ marginTop: 1, height: '88%' }}>
        <Table stickyHeader sx={{ minWidth: '100%' }} aria-label='customized table'>
          <TableHead>
            <TableRow>
              {headerCells.map((header) => (
                <StyledTableCell
                  align={header.align as TableCellProps['align']}
                  variant='head'
                  width={header.name === 'Requirement' ? 66 : undefined}
                >
                  {header.sortable ? (
                    <TableSortLabel
                      active={header.id === filter?.sortBy}
                      direction={filter?.sortDirection as 'desc' | 'asc'}
                      onClick={() => {
                        setFilter((prev) => ({
                          ...prev,
                          sortDirection: filter?.sortDirection === 'asc' ? 'desc' : 'asc',
                          sortBy: header.id as string,
                        }));
                      }}
                    >
                      {header.label}
                    </TableSortLabel>
                  ) : (
                    header.label
                  )}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {invites.map((invite, index) => (
              <StyledTableRow
                key={index}
                {...(invites.length === index + 1 && {
                  ref: lastElementRef,
                })}
              >
                <StyledTableCell>
                  <Avatar
                    variant='rounded'
                    alt={invite.user?.firstname}
                    src={invite.user?.picture?.thumbnail || ''}
                    sx={{ borderRadius: '50%' }}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                    <MuiLink to={`/users/${invite.userID}`}>
                      {invite.user?.firstname} {invite.user?.lastname}
                    </MuiLink>
                    {!!invite.offer?.incentiveValue && <Icons.DollarSignV2Icon />}
                  </Box>
                </StyledTableCell>
                <StyledTableCell width={220}>
                  <MuiLink to={`/offers/${invite.offerID}`}>{invite.offer?.title}</MuiLink>
                </StyledTableCell>
                <StyledTableCell>{dayjs(invite.updatedAt).format('ll')}</StyledTableCell>
                <StyledTableCell width={96}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    {REQUIREMENT_ICON_MAP[invite.requirements || ''] && (
                      <SvgIcon name={REQUIREMENT_ICON_MAP[invite.requirements || ''] as IconName} />
                    )}
                    <span>{REQUIREMENT_MAP[invite.requirements || ''] || '-'}</span>
                  </Box>
                </StyledTableCell>
                <StyledTableCell align='right'>
                  ${formatNumberWithCommas(invite.offerPrice || 0)}
                </StyledTableCell>
                <StyledTableCell align='center'>
                  <InviteStatus status={invite.status || ''} />
                </StyledTableCell>
                <StyledTableCell align='center'>
                  {invite.status === InviteType.available ? (
                    <Button
                      size='small'
                      variant='outlined'
                      color='error'
                      startIcon={<DeleteIcon />}
                      onClick={() => setCancelInvite(invite)}
                      sx={{
                        borderRadius: '6px',
                        borderWidth: '1px',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  ) : (
                    '-'
                  )}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={!!cancelInvite}
        onClose={() => setCancelInvite(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: '20px',
            minWidth: 400,
            maxWidth: 440,
          },
        }}
      >
        <DialogContent sx={{ pt: 1, paddingBottom: 0 }}>
          {cancelInvite && (
            <>
              <Avatar
                src={cancelInvite.user?.picture?.thumbnail || ''}
                alt={`${cancelInvite.user?.firstname} ${cancelInvite.user?.lastname}`}
                sx={{ width: 95, height: 95, margin: '0 auto 38px' }}
              />
              <Typography fontSize={'22px'} fontWeight={500} sx={{ marginBottom: '3px' }}>
                Are you sure you want to cancel the collab request?
              </Typography>
              <Typography variant='body2' color='text.secondary' fontSize={'15px'} sx={{ paddingBottom: '54px' }}>
                {cancelInvite.user?.firstname} {cancelInvite.user?.lastname} still has{' '}
                <span style={{ fontWeight: 500 }}>{hoursRemaining(cancelInvite.expiry)} hours to respond.</span> If you cancel, the{' '}
                <span style={{ fontWeight: 500 }}>
                  ${formatNumberWithCommas(cancelInvite.offerPrice || 0)}{' '}will be immediately refunded</span>
                {' '}to your account.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, paddingBottom: 3 }}>
                <Button
                  fullWidth
                  variant='contained'
                  color='error'
                  sx={{ height: '48px' }}
                  disabled={cancelInviteMutation.isPending}
                  onClick={() => {
                    if (cancelInvite) {
                      cancelInviteMutation.mutate({ id: Number(cancelInvite.id) });
                    }
                  }}
                >
                  {cancelInviteMutation.isPending ? 'Cancelling...' : 'Cancel Collab'}
                </Button>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    fullWidth
                    variant='outlined'
                    color='inherit'
                    sx={{ height: '48px' }}
                    onClick={() => setCancelInvite(null)}
                  >
                    Keep Request
                  </Button>
                </div>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const MuiLink = styled(Link)`
  color: #111111;
  font-size: 15px;
  font-weight: 400;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
